import { useState } from 'react';
import { useAppStore } from '../../../../store/appStore';
import type { CustomEditorColors } from '../../../../core/theme/customEditorColors';
import type { EditorColorSchemeId, EditorColorSchemeTokenColors } from '../../../../core/theme/monacoColorSchemes';
import { CustomVariantToggle } from './CustomVariantToggle';
import { CustomEditorColorsPanel } from './CustomEditorColorsPanel';

interface CustomEditorColorsSectionProps {
  /** The dialog's DRAFT custom color table (both variants). Never null while this section renders. */
  colors: CustomEditorColors;
  /** Commits `hex` to every token in `tokens`, for the currently-edited variant, in the draft. */
  onColorChange: (variant: 'light' | 'dark', tokens: readonly (keyof EditorColorSchemeTokenColors)[], hex: string) => void;
  /** Re-seeds BOTH draft variants from `scheme`'s preset tables. */
  onCopyFrom: (scheme: EditorColorSchemeId) => void;
}

/**
 * Editor colors dialog → Custom section (staged-draft dialog iteration).
 * Owns which variant (light/dark) is being edited — independent of the
 * app's own theme mode, defaulting to it on mount — and feeds that single
 * piece of state to both the toggle and the picker panel below it. Every
 * edit is forwarded to the dialog's draft via `onColorChange`/`onCopyFrom`;
 * this component never writes the store.
 */
export function CustomEditorColorsSection({ colors, onColorChange, onCopyFrom }: CustomEditorColorsSectionProps) {
  const isDarkMode = useAppStore((state) => state.isDarkMode);
  const [variant, setVariant] = useState<'light' | 'dark'>(isDarkMode ? 'dark' : 'light');

  return (
    <div className="space-y-2">
      <CustomVariantToggle variant={variant} onChange={setVariant} />
      <CustomEditorColorsPanel
        variant={variant}
        colors={colors[variant]}
        onColorChange={(tokens, hex) => onColorChange(variant, tokens, hex)}
        onCopyFrom={onCopyFrom}
      />
    </div>
  );
}
